/**
 * Included items table — the 9-column table inside each type
 * subsection. CSS Grid layout, one card-row per item. Columns:
 *
 *   #  | 标题 (EN+ZH)  | 期刊+abbr  | pubdate | doi | 备注 | JCR | IF | 新锐
 *
 * Desktop: single 9-column grid row per item, header on top.
 * Mobile (< 720px): collapses to a stacked definition list (no
 * horizontal scroll). The mobile definition view is achieved by
 * `data-density="compact"` + a CSS grid that re-orders itself via
 * `--grid-template-areas` overrides.
 *
 * JCR / IF / 新锐 columns are conditional — when the worker doesn't
 * surface them yet on a `WeekIncludedItem` the cells render "—" so
 * the column width stays stable.
 */

import { el } from "../dom";
import type { WeekIncludedItem } from "../types";

export interface IncludedItemsTableOptions {
  items: WeekIncludedItem[];
}

const COLUMNS = [
  { key: "ref_no", label: "#", minWidth: "44px" },
  { key: "title", label: "标题", minWidth: "auto" },
  { key: "journal", label: "期刊", minWidth: "auto" },
  { key: "pubdate", label: "发表日期", minWidth: "110px" },
  { key: "doi", label: "DOI", minWidth: "auto" },
  { key: "note", label: "备注", minWidth: "auto" },
  { key: "jcr", label: "JCR", minWidth: "60px" },
  { key: "if", label: "IF", minWidth: "70px" },
  { key: "nrt", label: "新锐", minWidth: "70px" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

export function includedItemsTable({ items }: IncludedItemsTableOptions): HTMLElement {
  const root = el("section", { class: "included-items-table" });

  if (items.length === 0) {
    root.appendChild(
      el("div", { class: "included-items-empty" }, "本周该疾病无纳入文献。"),
    );
    return root;
  }

  // CSS Grid uses `grid-template-columns: var(--included-grid)` from
  // components.css. We set the column template by inlining the value
  // to keep the table self-contained.
  const template = COLUMNS.map((c) => c.minWidth).join(" ");
  root.style.setProperty("--included-grid", template);

  const head = el("div", { class: "included-items-row included-items-head" });
  for (const col of COLUMNS) {
    head.appendChild(
      el("div", { class: `included-items-cell is-head data-${col.key}` }, col.label),
    );
  }
  root.appendChild(head);

  for (const it of items) {
    root.appendChild(renderRow(it));
  }

  return root;
}

function renderRow(it: WeekIncludedItem): HTMLElement {
  const row = el("div", { class: "included-items-row", "data-pmid": it.pmid });

  const titleZh = it.title_zh ?? "（暂无中文标题）";
  const note = it.note_zh ?? "";
  const journal = it.journal_abbr ?? it.journal ?? "—";
  const pubdate = it.pubdate || it.epdat || "—";
  const doi = it.doi ?? null;

  // # column — ref_no, right-aligned
  row.appendChild(
    el("div", { class: "included-items-cell data-ref_no" }, String(it.ref_no)),
  );

  // Title cell — bilingual, EN in serif; ZH in muted color.
  row.appendChild(
    el("div", { class: "included-items-cell data-title" }, [
      el("a", {
        class: "included-items-title-en",
        href: `#/article/${it.pmid}`,
      }, it.title),
      el("span", { class: "included-items-title-zh" }, titleZh),
    ]),
  );

  // Journal + abbr.
  row.appendChild(
    el("div", { class: "included-items-cell data-journal" }, [
      el("span", { class: "included-items-journal-name" }, it.journal ?? "—"),
      it.journal_abbr
        ? el("span", { class: "included-items-journal-abbr" }, it.journal_abbr)
        : null,
    ]),
  );

  row.appendChild(
    el("div", { class: "included-items-cell data-pubdate" }, pubdate),
  );

  // DOI cell.
  row.appendChild(
    el("div", { class: "included-items-cell data-doi" }, [
      doi
        ? el(
            "a",
            {
              class: "included-items-doi",
              href: `https://doi.org/${doi}`,
              target: "_blank",
              rel: "noopener noreferrer",
            },
            doi,
          )
        : el("span", { class: "muted" }, "—"),
    ]),
  );

  row.appendChild(
    el("div", { class: "included-items-cell data-note" }, note || "—"),
  );

  // Metric cells — when not present on the wire, show em-dash so the
  // column width stays predictable.
  row.appendChild(
    el("div", { class: "included-items-cell data-jcr" }, "—"),
  );
  row.appendChild(
    el("div", { class: "included-items-cell data-if" }, "—"),
  );
  row.appendChild(
    el("div", { class: "included-items-cell data-nrt" }, "—"),
  );

  return row;
}

// Re-export the column list so tests / future styling can verify
// structural invariants without re-typing the keys.
export const INCLUDED_COLUMNS: ReadonlyArray<ColumnKey> = COLUMNS.map((c) => c.key);